import { Box, Typography } from '@mui/material';
import { compareAgentVersions } from '../../content/agentDiff';
import type { DiffRow } from '../../content/agentDiff';
import { tokens } from '../../theme';
import type { VersionDiffProps } from './types';

/**
 * What differs between two versions of an agent, from the one compared against to
 * the one on screen: the title and summary, the methods supported, the prompt line by
 * line, and the evaluation cases.
 *
 * **An added line sits on pastel green and a removed one on pastel red, and colour is
 * never the only cue.** Red and green are the pair most often confused, so each
 * changed line also carries a sign in the gutter and a removed one is struck through:
 * the diff reads in greyscale and to someone who sees neither hue. The two grounds are
 * their own tokens, `diff.addedSurface` and `diff.removedSurface`, with a pair for each
 * mode — not the error ground, which is for failures and nothing else — and
 * `contrast.test.ts` holds the text and the sign on them to AA in both.
 *
 * The signs are `aria-hidden` and the meaning is carried by `<ins>` and `<del>`, the
 * elements HTML has for exactly this, so a screen reader is told which is which
 * rather than reading a bare "+".
 */

const GUTTER = { added: '+', removed: '−', same: '' } as const;

/** Theme paths, so each mode brings its own value and nothing here knows which. */
const BACKGROUND = {
  added: 'diff.addedSurface',
  removed: 'diff.removedSurface',
  same: 'transparent',
} as const;

/** One drawn line. */
const Row = ({ row }: { row: DiffRow }) => {
  if (row.kind === 'skipped') {
    return (
      <Box
        sx={{
          paddingBlock: 1,
          paddingInline: 3,
          color: 'text.meta',
          fontFamily: tokens.fontFamily.body,
          fontSize: 12,
        }}
      >
        <span aria-hidden>&#8943; </span>
        {row.count} unchanged lines
      </Box>
    );
  }

  const content = {
    added: <ins style={{ textDecoration: 'none' }}>{row.text}</ins>,
    removed: <del style={{ textDecoration: 'line-through' }}>{row.text}</del>,
    same: <span>{row.text}</span>,
  }[row.kind];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '20px minmax(0, 1fr)',
        backgroundColor: BACKGROUND[row.kind],
        color: 'text.primary',
        paddingInline: 3,
        // An empty line still has a height, or a blank line in a prompt vanishes from the diff.
        minHeight: '1.6em',
      }}
    >
      {/* `text.secondary`, not `text.meta`: the sign says which kind of change this is,
          so it is text that carries meaning and is held to AA on both grounds. */}
      <Box component="span" aria-hidden sx={{ userSelect: 'none', color: 'text.secondary' }}>
        {GUTTER[row.kind]}
      </Box>
      <Box component="span" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {content}
      </Box>
    </Box>
  );
};

/** The rows in the recessed mono block a prompt is drawn in. */
const Rows = ({ rows, label, scroll = false }: { rows: DiffRow[]; label: string; scroll?: boolean }) => (
  <Box
    role="region"
    aria-label={label}
    // Focusable when it can scroll, so a keyboard user can reach the rest of it.
    tabIndex={scroll ? 0 : undefined}
    sx={{
      fontFamily: tokens.fontFamily.mono,
      fontSize: 13,
      lineHeight: 1.6,
      backgroundColor: 'surface.sunken',
      borderRadius: `${String(tokens.radius.md)}px`,
      paddingBlock: 2,
      ...(scroll ? { maxHeight: 560, overflowY: 'auto' } : {}),
    }}
  >
    {rows.map((row, index) => (
      <Row key={`${String(index)}-${row.kind}`} row={row} />
    ))}
  </Box>
);

const Labelled = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Typography variant="overline" component="h3" sx={{ color: 'text.secondary', letterSpacing: '1px' }}>
      {label}
    </Typography>
    {children}
  </Box>
);

/** A before and an after, as a removed line over an added one. */
const replaced = (change: { from: string; to: string }): DiffRow[] => [
  { kind: 'removed', text: change.from },
  { kind: 'added', text: change.to },
];

export const VersionDiff = ({ from, to }: VersionDiffProps) => {
  const comparison = compareAgentVersions(from, to);

  if (comparison.identical) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Version {from.version} and version {to.version} are identical.
      </Typography>
    );
  }

  const methodRows: DiffRow[] = [
    ...comparison.methods.removed.map((text) => ({ kind: 'removed' as const, text })),
    ...comparison.methods.added.map((text) => ({ kind: 'added' as const, text })),
  ];

  const { added, removed, changed } = comparison.evaluations;
  const evaluationRows: DiffRow[] = [
    ...removed.map((text) => ({ kind: 'removed' as const, text })),
    ...added.map((text) => ({ kind: 'added' as const, text })),
    // A case present in both whose content differs: neither added nor removed, so it
    // is drawn as itself with the fact in words, which a sign could not carry.
    ...changed.map((name) => ({ kind: 'same' as const, text: `${name} (content changed)` })),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {comparison.title !== undefined && (
        <Labelled label="Title">
          <Rows rows={replaced(comparison.title)} label="Title changes" />
        </Labelled>
      )}

      {comparison.summary !== undefined && (
        <Labelled label="Summary">
          <Rows rows={replaced(comparison.summary)} label="Summary changes" />
        </Labelled>
      )}

      {methodRows.length > 0 && (
        <Labelled label="Supports">
          <Rows rows={methodRows} label="Method changes" />
        </Labelled>
      )}

      <Labelled label="Prompt">
        {comparison.promptChanged ? (
          <Rows rows={comparison.prompt} label="Prompt changes" scroll />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            The prompt is unchanged.
          </Typography>
        )}
      </Labelled>

      <Labelled label="Evaluations">
        {evaluationRows.length > 0 ? (
          <Rows rows={evaluationRows} label="Evaluation changes" />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            The evaluations are unchanged.
          </Typography>
        )}
      </Labelled>
    </Box>
  );
};
